const { hasAccess } = require("../js/authProvider");

const COOKIE_NAME = "guidelines_last_seen";
const NEW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const getStorageKey = userId => encodeURIComponent(String(userId == null ? "" : userId)) + "_" + COOKIE_NAME;

const readCookie = key => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find(part => part.startsWith(key + "="));
  return match ? decodeURIComponent(match.substring(key.length + 1)) : null;
};

/** Timestamp (ms) of the last visit of the given user to the guidelines page, or null when never visited. */
const getGuidelinesLastSeen = userId => {
  const key = getStorageKey(userId);
  let raw = readCookie(key);
  if (!raw && typeof localStorage !== "undefined") {
    // cookies are not available in every webview (e.g. file:// in Cordova)
    raw = localStorage.getItem(key);
  }
  const value = parseInt(raw, 10);
  return Number.isNaN(value) ? null : value;
};

const markGuidelinesSeen = (userId, timestamp = Date.now()) => {
  const key = getStorageKey(userId);
  const value = String(timestamp);
  if (typeof document !== "undefined") {
    document.cookie = key + "=" + encodeURIComponent(value) + "; max-age=" + 365 * 24 * 60 * 60 + "; path=/; SameSite=Lax";
  }
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore storage availability issues
  }
};

const getEntryCreatedTime = entry => {
  const parsed = new Date(entry.createdAt || entry.date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const ROLE_RESOURCE_HINTS = {
  admin: ["administration", "administration.*", "administration.products", "administration.products.*", "reports", "reports.*"],
  manager: ["administration", "administration.*", "stockmanagement", "stockmanagement.*", "sales", "sales.*", "reports", "reports.*"],
  cashier: ["sales", "sales.*", "orders", "orders.*"],
  superadmin: ["superadmin"]
};

const isEntryVisibleForUser = (entry, myStatus, rbac) => {
  const roles = myStatus?.roles || [];
  if (roles.some(role => String(role.role || "").toLowerCase() === "superadmin")) return true;

  const userRoles = roles
    .filter(role => !role.entityType || role.entityType === "Restaurant")
    .map(role => String(role.role || "").toLowerCase());
  const roleFilter = (Array.isArray(entry.roles) ? entry.roles : []).map(r => String(r).toLowerCase());
  const permissions = Array.isArray(entry.permissions) ? entry.permissions : [];
  const passesRole = roleFilter.length === 0 || roleFilter.some(role => userRoles.includes(role) || (ROLE_RESOURCE_HINTS[role] || []).some(resource => hasAccess(resource, rbac)));
  const passesPermission = permissions.length === 0 || permissions.some(permission => hasAccess(permission, rbac));
  return passesRole && passesPermission;
};

/**
 * True when there is a visible entry created after the last visit. Without a previous visit,
 * entries created within the last NEW_DAYS days count as new.
 */
const hasUnseenGuidelines = (entries, lastSeen, myStatus, rbac, now = Date.now()) => {
  if (!Array.isArray(entries)) return false;
  const threshold = lastSeen != null ? lastSeen : now - NEW_DAYS * DAY_MS;
  return entries.some(entry => {
    const created = getEntryCreatedTime(entry);
    return created != null && created > threshold && isEntryVisibleForUser(entry, myStatus, rbac);
  });
};

module.exports = { COOKIE_NAME, getGuidelinesLastSeen, markGuidelinesSeen, hasUnseenGuidelines };
